import { useEffect, useState } from "react";
import { Truck, Plus, PencilSimple, Trash, Buildings, CalendarCheck } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ESITI_FORNITORE, DEFAULT_SUPPLIER_CRITERIA } from "@/lib/constants";
import { toast } from "sonner";

const emptyForm = () => ({
  tenant_id: "", ragione_sociale: "", piva: "", categoria: "", referente: "", email: "", telefono: "",
  criteri: DEFAULT_SUPPLIER_CRITERIA.map((label) => ({ label, esito: false })),
  esito: "qualificato", data_valutazione: "", data_riqualifica: "", note: "",
});

const esitoBadge = (e) => {
  const map = {
    "qualificato": "bg-emerald-100 text-emerald-700 border-emerald-200",
    "qualificato con riserva": "bg-amber-100 text-amber-800 border-amber-200",
    "non qualificato": "bg-rose-100 text-rose-700 border-rose-200",
  };
  return <Badge className={`${map[e] || ""} capitalize`}>{e}</Badge>;
};
const riqualBadge = (ds) => {
  if (!ds) return null;
  const days = Math.ceil((new Date(ds + "T00:00:00") - new Date()) / 86400000);
  if (days < 0) return <Badge className="bg-red-100 text-red-700 border-red-200">Riqualifica scaduta</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Riqualifica tra {days} gg</Badge>;
  return null;
};

export default function Suppliers() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    setLoading(true);
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/suppliers${p}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const openNew = () => { setEditingId(null); setForm({ ...emptyForm(), tenant_id: tenantId || "" }); setOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ ...emptyForm(), ...s, criteri: (s.criteri || []).map((c) => ({ ...c })) });
    setOpen(true);
  };

  const liveScore = () => {
    const total = form.criteri.length;
    return total ? Math.round(100 * form.criteri.filter((c) => c.esito).length / total) : 0;
  };
  const toggleCrit = (i) => setForm((f) => ({ ...f, criteri: f.criteri.map((c, idx) => idx === i ? { ...c, esito: !c.esito } : c) }));
  const editCritLabel = (i, v) => setForm((f) => ({ ...f, criteri: f.criteri.map((c, idx) => idx === i ? { ...c, label: v } : c) }));
  const addCrit = () => setForm((f) => ({ ...f, criteri: [...f.criteri, { label: "", esito: false }] }));
  const removeCrit = (i) => setForm((f) => ({ ...f, criteri: f.criteri.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.tenant_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.ragione_sociale.trim()) { toast.error("Inserisci la ragione sociale"); return; }
    const payload = { ...form, criteri: form.criteri.filter((c) => c.label.trim()) };
    delete payload.id; delete payload.created_at; delete payload.punteggio;
    try {
      if (editingId) { await api.put(`/suppliers/${editingId}`, payload); toast.success("Fornitore aggiornato"); }
      else { await api.post("/suppliers", payload); toast.success("Fornitore creato"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo fornitore?")) return;
    try { await api.delete(`/suppliers/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const orgName = (tid) => organizations.find((o) => o.id === tid)?.nome || "";

  return (
    <div className="space-y-6">
      <PageHero label="UNI EN ISO 9001:2015" title="Qualifica fornitori"
        subtitle={tenantId ? currentOrg?.nome : "Valutazione e riqualifica dei fornitori con checklist e punteggio"}>
        {isConsultant && (
          <Button onClick={openNew} data-testid="new-supplier-btn"
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuovo fornitore
          </Button>
        )}
      </PageHero>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Truck size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun fornitore registrato{tenantId ? "" : ". Seleziona un cliente per aggiungerne"}.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="suppliers-list">
          {items.map((s) => (
            <Card key={s.id} data-testid={`supplier-${s.id}`} className="p-5 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{s.ragione_sociale}</h3>
                  <div className="text-sm text-slate-500">{[s.categoria, s.piva && `P.IVA ${s.piva}`].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-500">
                    {!tenantId && isConsultant && <span className="inline-flex items-center gap-1"><Buildings size={13} /> {orgName(s.tenant_id)}</span>}
                    {s.referente && <span>Ref. {s.referente}</span>}
                    {s.data_valutazione && <span className="inline-flex items-center gap-1"><CalendarCheck size={13} /> val. {s.data_valutazione}</span>}
                  </div>
                </div>
                {isConsultant && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-supplier-${s.id}`} onClick={() => openEdit(s)}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`delete-supplier-${s.id}`} onClick={() => remove(s.id)}><Trash size={16} /></Button>
                  </div>
                )}
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-500">Punteggio checklist</span>
                  <span className="font-semibold">{s.punteggio}%</span>
                </div>
                <Progress value={s.punteggio} className="h-2" />
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {esitoBadge(s.esito)}
                {riqualBadge(s.data_riqualifica)}
                {s.data_riqualifica && <span className="text-xs text-slate-500">Riqualifica: {s.data_riqualifica}</span>}
              </div>
              {(s.criteri || []).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3">
                  {s.criteri.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className={`h-2 w-2 rounded-full ${c.esito ? "bg-emerald-500" : "bg-slate-300"}`} />
                      {c.label}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {!editingId && (
              <div>
                <Label>Cliente</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="supplier-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Ragione sociale</Label><Input value={form.ragione_sociale} data-testid="supplier-nome" onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>P.IVA</Label><Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Categoria / servizio</Label><Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Referente</Label><Input value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Checklist di qualifica</Label>
                <span className="text-sm font-semibold text-indigo-700">{liveScore()}%</span>
              </div>
              <div className="mt-2 space-y-2">
                {form.criteri.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Checkbox checked={c.esito} onCheckedChange={() => toggleCrit(i)} data-testid={`supplier-crit-${i}`} />
                    <Input value={c.label} onChange={(e) => editCritLabel(i, e.target.value)} className="h-8 flex-1" placeholder="Criterio" />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeCrit(i)}><Trash size={14} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addCrit}><Plus className="h-3.5 w-3.5 mr-1.5" /> Aggiungi criterio</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Esito</Label>
                <Select value={form.esito} onValueChange={(v) => setForm({ ...form, esito: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="supplier-esito"><SelectValue /></SelectTrigger>
                  <SelectContent>{ESITI_FORNITORE.map((e) => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data valutazione</Label><Input type="date" value={form.data_valutazione || ""} onChange={(e) => setForm({ ...form, data_valutazione: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Data riqualifica</Label><Input type="date" value={form.data_riqualifica || ""} data-testid="supplier-riqualifica" onChange={(e) => setForm({ ...form, data_riqualifica: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-supplier-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
